import { Link } from 'react-router-dom';
import type { ScheduleStudent } from '@/pages/dashboard/components/WeekScheduleCard';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

function todayLabel(): string {
  return DAY_LABELS[new Date().getDay()];
}

function tomorrowLabel(): string {
  return DAY_LABELS[(new Date().getDay() + 1) % 7];
}

export interface TodoStudent {
  id: string;
  name: string;
}

export interface TodoExam {
  id: string;
  studentId: string;
  studentName: string;
  title: string;
}

interface TodayTodosCardProps {
  students: ScheduleStudent[];
  /** 오늘 수업일지가 이미 작성된 학생 id 목록 */
  writtenStudentIds: string[];
  unpaidStudents: TodoStudent[];
  tomorrowExams: TodoExam[];
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

interface TodoRowProps {
  icon: string;
  toneClass: string;
  label: string;
  countText: string;
  empty: boolean;
  children: React.ReactNode;
}

function TodoRow({ icon, toneClass, label, countText, empty, children }: TodoRowProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${toneClass}`}>
            <i className={`${icon} text-sm`}></i>
          </span>
          <p className="text-sm font-semibold text-foreground-900">{label}</p>
        </div>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
            empty ? 'bg-background-100 text-foreground-500' : 'bg-primary-100 text-primary-700'
          }`}
        >
          {countText}
        </span>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {empty ? <span className="text-xs text-foreground-400">없음</span> : children}
      </div>
    </div>
  );
}

export default function TodayTodosCard({
  students,
  writtenStudentIds,
  unpaidStudents,
  tomorrowExams,
}: TodayTodosCardProps) {
  const day = todayLabel();
  const written = new Set(writtenStudentIds);
  const missingLog = students.filter(
    (s) => (s.class_days ?? []).includes(day) && !written.has(s.id),
  );

  const total = missingLog.length + unpaidStudents.length + tomorrowExams.length;
  const date = todayStr();

  return (
    <section className="rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 to-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-checkbox-multiple-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">오늘 할 일</h2>
        </div>
        {total > 0 ? (
          <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-800">
            처리할 일 {total}건
          </span>
        ) : (
          <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            모두 처리 완료
          </span>
        )}
      </div>

      {total === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-primary-200 bg-background-50 py-10">
          <i className="ri-checkbox-circle-line text-2xl text-primary-500"></i>
          <p className="text-sm text-foreground-600">오늘 처리할 일이 없어요. 여유로운 하루 보내세요.</p>
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {missingLog.length > 0 && (
            <TodoRow
              icon="ri-edit-box-line"
              toneClass="bg-accent-100 text-accent-700"
              label={`오늘(${day}) 일지 안 쓴 학생`}
              countText={`${missingLog.length}명`}
              empty={false}
            >
              {missingLog.map((s) => (
                <Link
                  key={s.id}
                  to={`/lessons/new?student=${s.id}&date=${date}`}
                  className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-700 transition hover:border-accent-300 hover:bg-accent-100 hover:text-accent-800 whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  {s.name}
                </Link>
              ))}
            </TodoRow>
          )}

          {unpaidStudents.length > 0 && (
            <TodoRow
              icon="ri-error-warning-line"
              toneClass="bg-secondary-100 text-secondary-800"
              label="교습비 미납 학생"
              countText={`${unpaidStudents.length}명`}
              empty={false}
            >
              {unpaidStudents.map((s) => (
                <Link
                  key={s.id}
                  to="/tuition"
                  className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-700 transition hover:border-secondary-300 hover:bg-secondary-100 hover:text-secondary-900 whitespace-nowrap"
                >
                  <i className="ri-user-3-line"></i>
                  {s.name}
                </Link>
              ))}
            </TodoRow>
          )}

          {tomorrowExams.length > 0 && (
            <TodoRow
              icon="ri-calendar-event-line"
              toneClass="bg-primary-100 text-primary-700"
              label={`내일(${tomorrowLabel()}) 시험`}
              countText={`${tomorrowExams.length}건`}
              empty={false}
            >
              {tomorrowExams.map((e) => (
                <Link
                  key={e.id}
                  to={`/students/${e.studentId}`}
                  className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-700 transition hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700 whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line"></i>
                  {e.studentName} · {e.title || '시험'}
                </Link>
              ))}
            </TodoRow>
          )}
        </div>
      )}
    </section>
  );
}